import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Stack,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  X,
  ChevronLeft,
  ChevronRight,
  GalleryHorizontalEnd,
  ZoomIn,
  ZoomOut,
  Download
} from 'lucide-react';

function PreviewGallery({ open, onClose, submissionId }) {
  const [previews, setPreviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (open && submissionId) {
      fetchPreviews();
    }
  }, [open, submissionId]);

  const fetchPreviews = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/submissions/${submissionId}/documents/previews`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPreviews(data);
        setCurrentIndex(0);
        setZoom(1);
      }
    } catch (err) {
      console.error('Fetch previews error:', err);
    }
    setLoading(false);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : previews.length - 1));
    setZoom(1);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < previews.length - 1 ? prev + 1 : 0));
    setZoom(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') handlePrev();
    if (e.key === 'ArrowRight') handleNext();
    if (e.key === 'Escape') onClose();
  };

  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.25, 3));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.25, 0.5));

  const currentPreview = previews[currentIndex];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen
      onKeyDown={handleKeyDown}
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0, 0, 0, 0.95)',
          backgroundImage: 'none'
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 10,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.8), transparent)'
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <GalleryHorizontalEnd size={24} color="#8b5cf6" />
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            Planches Preview
          </Typography>
          {previews.length > 0 && (
            <Chip
              label={`${currentIndex + 1} / ${previews.length}`}
              size="small"
              sx={{ bgcolor: 'rgba(139, 92, 246, 0.2)', color: '#8b5cf6' }}
            />
          )}
        </Stack>

        <Stack direction="row" spacing={1}>
          {currentPreview && (
            <>
              <IconButton
                onClick={handleZoomOut}
                sx={{ color: 'white' }}
                disabled={zoom <= 0.5}
              >
                <ZoomOut size={20} />
              </IconButton>
              <IconButton
                onClick={handleZoomIn}
                sx={{ color: 'white' }}
                disabled={zoom >= 3}
              >
                <ZoomIn size={20} />
              </IconButton>
              <IconButton
                onClick={() => window.open(currentPreview.url, '_blank')}
                sx={{ color: 'white' }}
              >
                <Download size={20} />
              </IconButton>
            </>
          )}
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <X size={24} />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 0,
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        {loading ? (
          <CircularProgress sx={{ color: '#8b5cf6' }} />
        ) : previews.length === 0 ? (
          <Box sx={{ textAlign: 'center', color: 'white' }}>
            <GalleryHorizontalEnd size={64} color="#6b7280" style={{ marginBottom: 16 }} />
            <Typography variant="h6">Aucune planche preview</Typography>
            <Typography variant="body2" color="text.secondary">
              Marquez des images comme "Preview" dans les Documents
            </Typography>
          </Box>
        ) : (
          <>
            {/* Navigation Arrows */}
            {previews.length > 1 && (
              <>
                <IconButton
                  onClick={handlePrev}
                  sx={{
                    position: 'absolute',
                    left: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                    width: 56,
                    height: 56
                  }}
                >
                  <ChevronLeft size={32} />
                </IconButton>
                <IconButton
                  onClick={handleNext}
                  sx={{
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                    width: 56,
                    height: 56
                  }}
                >
                  <ChevronRight size={32} />
                </IconButton>
              </>
            )}

            {/* Image */}
            <Box
              sx={{
                maxWidth: '90vw',
                maxHeight: '85vh',
                overflow: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Box
                component="img"
                src={currentPreview.url}
                alt={currentPreview.name}
                sx={{
                  maxWidth: `${90 * zoom}vw`,
                  maxHeight: `${85 * zoom}vh`,
                  objectFit: 'contain',
                  transition: 'transform 0.2s',
                  cursor: zoom > 1 ? 'grab' : 'default'
                }}
              />
            </Box>

            {/* Image Name */}
            <Typography
              variant="body2"
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'rgba(255, 255, 255, 0.7)',
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                px: 2,
                py: 0.5,
                borderRadius: 1
              }}
            >
              {currentPreview.name}
            </Typography>
          </>
        )}
      </DialogContent>

      {/* Thumbnails Bar */}
      {previews.length > 1 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 60,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1,
            p: 1,
            bgcolor: 'rgba(0, 0, 0, 0.6)',
            borderRadius: 2,
            maxWidth: '80vw',
            overflowX: 'auto'
          }}
        >
          {previews.map((preview, index) => (
            <Box
              key={preview.id}
              onClick={() => { setCurrentIndex(index); setZoom(1); }}
              sx={{
                width: 60,
                height: 60,
                borderRadius: 1,
                overflow: 'hidden',
                cursor: 'pointer',
                border: index === currentIndex ? '2px solid #8b5cf6' : '2px solid transparent',
                opacity: index === currentIndex ? 1 : 0.6,
                transition: 'all 0.2s',
                flexShrink: 0,
                '&:hover': { opacity: 1 }
              }}
            >
              <Box
                component="img"
                src={preview.url}
                alt={preview.name}
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </Box>
          ))}
        </Box>
      )}
    </Dialog>
  );
}

export default PreviewGallery;
